import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Title, Fieldset, Button, Control, Field } from "rbx";
import { useMutation, useApolloClient } from "react-apollo";

import {
  CREATE_PAYMENT_TERM_MUTATION,
  UPDATE_PAYMENT_TERM_MUTATION,
  DELETE_PAYMENT_TERM_MUTATION,
  IS_PAYMENT_TERM_BEING_USED
} from "../../graphql/PaymentTerms/PaymentTerms.graphql";
import { ourToast as toast } from "../../utils";
import PaymentTermForm from "./PaymentTermForm";
import { ModalContext } from "../ModalProvider";
import { Confirmation } from "../modals";

const DEFAULT_STATE = {
  TermCode: "",
  TermDescription: "",
  TermDays: 0,
  TermDiscount: 0,
  Active: true
};

const AddPaymentTermForm = ({ onComplete, data }) => {
  const [inputs, setInputs] = useState({ ...DEFAULT_STATE });
  const [editing, setEditing] = useState(false);
  const client = useApolloClient();
  const { setModalOpen } = useContext(ModalContext);

  useEffect(() => {
    if (data && data.RecId) {
      setInputs({
        TermCode: data.TermCode,
        TermDescription: data.TermDescription,
        TermDays: data.TermDays,
        TermDiscount: data.TermDiscount,
        Active: data.Active
      });
      setEditing(true);
    }
  }, [data]);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInputs({
      ...inputs,
      [e.target.name]:
        e.target.name === "TermDays" || e.target.name === "TermDiscount"
          ? parseInt(value)
          : value
    });
  };

  const [createPaymentTerm, { loading }] = useMutation(
    CREATE_PAYMENT_TERM_MUTATION,
    {
      refetchQueries: ["ALL_PAYMENT_TERMS_QUERY", "SINGLE_PAYMENT_TERM_QUERY"]
    }
  );
  const [updatePaymentTerm, res] = useMutation(UPDATE_PAYMENT_TERM_MUTATION, {
    refetchQueries: ["ALL_PAYMENT_TERMS_QUERY", "SINGLE_PAYMENT_TERM_QUERY"]
  });

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (data && data.RecId) {
        await updatePaymentTerm({
          variables: {
            data: {
              TermCode: inputs.TermCode,
              TermDescription: inputs.TermDescription,
              TermDays: inputs.TermDays,
              TermDiscount: inputs.TermDiscount,
              Active: inputs.Active
            },
            where: { RecId: parseInt(data.RecId) }
          }
        });
        toast.success("Payment Term edited successfully.");
      } else {
        await createPaymentTerm({
          variables: {
            data: {
              ...inputs
            }
          }
        });
        toast.success("Payment Term created successfully.");
      }
      onComplete();
    } catch (err) {
      console.log(err.message);
    }
  };

  const handleDelete = async e => {
    e.preventDefault();
    try {
      const {
        data: { IsPaymentTermBeingUsed }
      } = await client.query({
        query: IS_PAYMENT_TERM_BEING_USED,
        variables: {
          RecId: parseInt(data.RecId)
        },
        fetchPolicy: "network-only"
      });
      if (IsPaymentTermBeingUsed) {
        toast.warning(
          "This Payment Term is currently being used but may be made inactive."
        );
        setInputs({
          ...inputs,
          Active: false
        });
      } else {
        const performDelete = async () => {
          setModalOpen(false, "");
          await client.mutate({
            mutation: DELETE_PAYMENT_TERM_MUTATION,
            variables: {
              where: {
                RecId: parseInt(data.RecId)
              }
            },
            refetchQueries: [
              "ALL_PAYMENT_TERMS_QUERY",
              "SINGLE_PAYMENT_TERM_QUERY"
            ]
          });
          toast.success("Payment Term deleted successfully.");
        };
        setModalOpen(
          true,
          <Confirmation
            message="Are you sure you want to delete?"
            onConfirm={performDelete}
          />
        );
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  return (
    <form
      className="add-item-form"
      id="add-item-form"
      style={{ width: 600, maxWidth: "100%" }}
      onSubmit={handleSave}
    >
      <Title>{data?.RecId ? "Edit" : "Create"} Payment Term</Title>
      <Fieldset disabled={loading || res.loading}>
        <PaymentTermForm
          editing={editing}
          inputs={inputs}
          onChange={handleChange}
        />
        <hr />
        <Field kind="group">
          <Control expanded>
            <Field kind="group">
              <Control>
                <Button color="success" form="add-item-form" type="submit">
                  Save
                </Button>
              </Control>
              <Control>
                <Button type="button" onClick={onComplete}>
                  Cancel
                </Button>
              </Control>
            </Field>
          </Control>
          {data?.RecId && (
            <Control>
              <Button color="danger" onClick={handleDelete}>
                Delete
              </Button>
            </Control>
          )}
        </Field>
      </Fieldset>
    </form>
  );
};

AddPaymentTermForm.propTypes = {
  onComplete: PropTypes.func,
  data: PropTypes.object
};
export default AddPaymentTermForm;
